import { NgFor } from '@angular/common'
import { Component, Input, forwardRef, Output, EventEmitter, OnInit, Injector } from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms'
import { Venue } from '../domain/Venue';
import { VenueColourDirective } from '../directives/venue-colour.directive'

@Component({
    selector: 'app-multiple-choice-input',
    standalone: true,
    imports: [NgFor, VenueColourDirective],
    templateUrl: './multiple-choice-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultipleChoiceInputComponent),
            multi: true
        }
    ]
})
export class MultipleChoiceInputComponent implements ControlValueAccessor, OnInit {

    @Input() venue!: Venue
    @Input() options: string[] = []
    @Input() title?: string
    @Output() closeRequested = new EventEmitter<void>()

    public ngControl!: NgControl
    
    private _value: string | null = null
    private _selectedOptions: string[] = []
    disabled = false
    noneSelected = false

    private onChange = (value: string | null) => {}
    private onTouched = () => {};

    constructor(private injector: Injector) { }

   ngOnInit(): void {
        this.ngControl = this.injector.get(NgControl)
        if (this.ngControl) {
            this.ngControl.valueAccessor = this
        }
    }

    get selectedOptions(): string[] {
        return this._selectedOptions
    }

    get sortedOptions(): string[] {
        return [...this.options].sort()
    }

    get isRequired(): boolean {
        if (this.ngControl?.control?.validator) {
            const validator = this.ngControl.control.validator(new FormControl())
            return validator && validator['required']
        }
        return false
    }

    writeValue(value: string | null): void {
        this._value = value
        this._selectedOptions = this.parseValue(value)
        this.noneSelected = this._selectedOptions.length === 1
            && this._selectedOptions[0] === 'None'
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }

    isSelected(option: string): boolean {
        return this._selectedOptions.includes(option)
    }

    selectNone(): void {
        if (this.disabled) {
            return
        }
        this._selectedOptions = ['None']
        this._value = 'None'
        this.onChange(this._value)
        this.onTouched()
        this.noneSelected = this._selectedOptions.length === 1 && this._selectedOptions[0] === 'None'
    }

    toggleOption(option: string): void {
        if (this.disabled){
            return
        }

        const currentSelection = [...this._selectedOptions]
        const index = currentSelection.indexOf(option)
        
        if (index > -1) {
            currentSelection.splice(index, 1)
        } else {
            currentSelection.push(option)
        }

        // if 'None' is selected, remove it
        const noneIndex = currentSelection.indexOf('None')
        if (noneIndex > -1) {
            currentSelection.splice(noneIndex, 1)
        }

        // if no options are selected, and we are required, add 'None'
        if (currentSelection.length === 0 && this.isRequired) {
            currentSelection.push('None')
        }

        currentSelection.sort()
        
        this._selectedOptions = currentSelection
        this._value = currentSelection.length > 0 ? currentSelection.join(',') : null
        this.onChange(this._value)
        this.onTouched()
        this.noneSelected = this._selectedOptions.length === 1 && this._selectedOptions[0] === 'None'
    }

    clear(): void {
        if (this.disabled) {
            return
        }
        if (this.isRequired) {
            this._selectedOptions = ['None']
            this._value = 'None'
        } else {
            this._selectedOptions = []
            this._value = null
        }
        this.onChange(this._value)
        this.onTouched()
        this.noneSelected = this._selectedOptions.length === 1 && this._selectedOptions[0] === 'None'
    }

    private parseValue(value: string | null): string[] {
        if (!value) return []
        return value.split(',').filter(item => item.trim().length > 0).sort()
    }

    closePopover(): void {
        this.closeRequested.emit()
    }
}
